import { FormattedMessage, IntlProvider } from "react-intl";
import jobMessages from "../translate/jobMessages";
import { useContext } from "react";
import { LangContext } from "../App";
import "bootstrap/dist/css/bootstrap.css";
import Offer from "../images/offer.png";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";

const styles = {
  wrapper: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    padding: "80px 24px",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    width: "100%",
  },
  headerZone: {
    textAlign: "center",
    marginBottom: "50px",
  },
  headerP: {
    fontSize: "18px",
    color: "#4b5563",
  },
  mainTitle: {
    fontSize: "36px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.02em",
    margin: "0 0 16px 0",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "32px",
    alignItems: "start",
    width: "100%",
  },
  card: {
    background: "#ffffff",
    borderRadius: "24px",
    padding: "36px",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.04)",
    border: "1px solid #e2e8f0",
    height: "100%",
    boxSizing: "border-box",
  },
  cardHeaderGradient: {
    background: "linear-gradient(135deg, #1E3A5F 0%, #274C77 100%)",
    padding: "10px",
    borderRadius: "22px",
    textAlign: "left",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cardTitleGradient: {
    color: "#fff",
    margin: 0,
    fontSize: "20px",
  },
  cardTitleGradient: {
    color: "#fff",
    margin: 0,
  },
  imageContainer: {
    gridColumn: "1 / -1",
    textAlign: "center",
    marginTop: "50px",
  },
  imgStyle: {
    maxWidth: "100%",
    width: "400px",
    height: "auto",
    borderRadius: "20px",
    boxShadow: "0 20px 40px -20px rgba(0,0,0,0.1)",
  },
};

const Job = () => {
  const language = useContext(LangContext);

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            name: "Oferta usług Fullstack Developera",
            url: "https://michaljaskulski.pl/oferta",
            itemListElement: [
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Aplikacje webowe w React",
                  description:
                    "Projektowanie i tworzenie nowoczesnych aplikacji SPA w React, Vite i Next.js.",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Backend API (Node.js / PHP)",
                  description:
                    "Tworzenie skalowalnych backendów, REST API oraz integracji z bazami danych.",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Systemy biznesowe dla firm",
                  description:
                    "Dedykowane systemy usprawniające procesy biznesowe i raportowanie.",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Bazy danych MySQL i MSSQL",
                  description:
                    "Projektowanie struktur, optymalizacja zapytań, migracje i backupy.",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Konfiguracja i utrzymanie serwerów VPS",
                  description:
                    "Linux, Docker, SSL, monitoring i utrzymanie środowisk produkcyjnych.",
                },
              },
            ],
          })}
        </script>
        <title>Internetowe aplikacje bazodanowe | Strony internetowe</title>
        <meta
          name="description"
          content="Nowoczesne i bezpieczne responsywne aplikacje bazodanowe"
        />
      </Helmet>

      <div className="bodyBackground" style={styles.wrapper}>
        <IntlProvider locale={language} messages={jobMessages[language]}>
          <AnimatePresence>
            <motion.div
              style={styles.container}
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={textVariants}
            >
              <div style={styles.headerZone}>
                <h1 style={styles.mainTitle}>
                  <FormattedMessage id="offer" />
                </h1>
              </div>
              <div style={styles.grid}>
                <div style={styles.card}>
                  <div style={styles.cardHeaderGradient}>
                    <h2 style={styles.cardTitleGradient}>
                      <FormattedMessage id="webapp" />
                    </h2>
                  </div>

                  {language === "pl" ? (
                    <>
                      <div style={styles.sectionSubtitle}>
                        Potrafię zaprojektować i zrealizować aplikacje
                        internetowe, dzięki którym wszystkie potrzebne dane będą
                        dostępne w jednym miejscu i będą bezpieczne.
                      </div>
                      <br />
                      <div style={styles.sectionSubtitle}>
                        Jeśli Twoja firma:
                      </div>
                      <ul style={styles.list}>
                        <li style={styles.listItem}>
                          gromadzi dane w teczkach lub arkuszach kalkulacyjnych,
                          a wyszukiwanie danych jest uciążliwe
                        </li>
                        <li style={styles.listItem}>
                          posiada bazę dokumentów, które wymagają obiegu w
                          firmie
                        </li>
                        <li style={styles.listItem}>
                          potrzebuje koordynacji działań oraz uprawnień między
                          Zespołami w istniejącym systemie, którego nie da się
                          łatwo zmienić
                        </li>
                        <li style={styles.listItem}>
                          potrzebuje wsparcia w rozwoju i utrzymaniu środowiska
                          –{" "}
                          <strong>
                            <a href="/kontakt">skontaktuj się ze mną</a>
                          </strong>
                          .
                        </li>
                      </ul>
                      <p style={styles.headerP}>
                        Zaproponuję najlepsze rozwiązanie, które przyniesie
                        wymierne korzyści dla Twojej Firmy.
                      </p>
                    </>
                  ) : (
                    <>
                      <div style={styles.sectionSubtitle}>
                        I will design a solution for you, thanks to which all
                        your necessary data will be available in one place and
                        will be safe.
                      </div>
                      <div style={styles.sectionSubtitle}>If your company:</div>
                      <ul style={styles.list}>
                        <li style={styles.listItem}>
                          collects data in files or spreadsheets, and searching
                          for data is burdensome
                        </li>
                        <li style={styles.listItem}>
                          has a database of documents that require circulation
                          in your company
                        </li>
                        <li style={styles.listItem}>
                          needs coordination of activities and separate
                          permissions for your data within an unalterable system
                        </li>
                        <li style={styles.listItem}>
                          needs technical support in the development and
                          maintenance – please <strong>contact me</strong>.
                        </li>
                      </ul>
                      <p style={styles.headerP}>
                        I will propose the best solution for you, that will
                        bring tangible benefits to your company.
                      </p>
                    </>
                  )}
                </div>

                {/* KARTA 2: implementation */}
                <div style={styles.card}>
                  <div style={styles.cardHeaderGradient}>
                    <h2 style={styles.cardTitleGradient}>
                      <FormattedMessage id="implementation" />
                    </h2>
                  </div>

                  <div style={styles.sectionSubtitle}>
                    {language === "pl"
                      ? "Wdrożenia systemów ERP realizuję poprzez:"
                      : "I implement ERP systems through:"}
                  </div>
                  <br />
                  <ul style={styles.list}>
                    {language === "pl" ? (
                      <>
                        <li style={styles.listItem}>
                          Zrozumienie procesów biznesowych i integrację ze
                          starymi systemami.
                        </li>
                        <li style={styles.listItem}>
                          Konfigurację systemu ERP zgodnie z niestandardowymi
                          potrzebami organizacji.
                        </li>
                        <li style={styles.listItem}>
                          Określanie parametrów systemowych oraz uprawnień i ról
                          użytkowników.
                        </li>
                        <li style={styles.listItem}>
                          Szkolenie, wsparcie techniczne oraz przygotowanie
                          materiałów instruktażowych.
                        </li>
                        <li style={styles.listItem}>
                          Prowadzenie testów integracyjnych, jakościowych i
                          zarządzanie zmianą.
                        </li>
                        <li style={styles.listItem}>
                          Koordynację zespołu projektowego oraz tworzenie
                          wskaźników wydajności (KPI).
                        </li>
                      </>
                    ) : (
                      <>
                        <li style={styles.listItem}>
                          Understanding business processes and integration with
                          existing systems.
                        </li>
                        <li style={styles.listItem}>
                          Appropriate configuration of the ERP system based on
                          custom business needs.
                        </li>
                        <li style={styles.listItem}>
                          system parameters, authorizations, and user roles.
                        </li>
                        <li style={styles.listItem}>
                          user support, and preparing comprehensive
                          instructional materials.
                        </li>
                        <li style={styles.listItem}>
                          Conducting integration and quality tests alongside
                          change management.
                        </li>
                        <li style={styles.listItem}>
                          Coordinating the project team and creating performance
                          indicator reports.
                        </li>
                      </>
                    )}
                  </ul>
                </div>

                {/* KARTA 3: website */}
                <div style={styles.card}>
                  <div style={styles.cardHeaderGradient}>
                    <h2 style={styles.cardTitleGradient}>
                      <FormattedMessage id="website" />
                    </h2>
                  </div>

                  <ul style={styles.list}>
                    {language === "pl" ? (
                      <>
                        <li style={styles.listItem}>
                          Strony internetowe są realizowane zgodnie z
                          indywidualnie ustalonym projektem.
                        </li>
                        <li style={styles.listItem}>
                          Zapewniam nowoczesny, responsywny wygląd i wysoką
                          jakość wykonania.
                        </li>
                        <li style={styles.listItem}>
                          Oferuję możliwość samodzielnej edycji treści przez
                          zamawiającego (CMS).
                        </li>
                        <li style={styles.listItem}>
                          Gwarantuję łatwą obsługę i kompleksowe wsparcie
                          techniczne po wdrożeniu.
                        </li>
                      </>
                    ) : (
                      <>
                        <li style={styles.listItem}>
                          I create websites according to an individually agreed
                          project.
                        </li>
                        <li style={styles.listItem}>
                          I provide a modern, responsive look and high quality
                          of workmanship.
                        </li>
                        <li style={styles.listItem}>
                          I offer the possibility of independent content
                          management (CMS).
                        </li>
                        <li style={styles.listItem}>
                          I guarantee easy operation and long-term technical
                          support after release.
                        </li>
                      </>
                    )}
                  </ul>
                </div>

                {/* KARTA 4: development */}
                <div style={styles.card}>
                  <div style={styles.cardHeaderGradient}>
                    <h2 style={styles.cardTitleGradient}>
                      <FormattedMessage id="development" />
                    </h2>
                  </div>
                  <div style={styles.introText}>
                    <FormattedMessage
                      id="developmentDescription"
                      values={{ br: <br /> }}
                    />
                  </div>
                </div>

                {/* KARTA 5: managerit */}
                <div style={styles.card}>
                  <div style={styles.cardHeaderGradient}>
                    <h2 style={styles.cardTitleGradient}>
                      <FormattedMessage id="managerit" />
                    </h2>
                  </div>
                  <div style={styles.introText}>
                    <FormattedMessage
                      id="manageritDescription"
                      values={{ br: <br /> }}
                    />
                  </div>
                </div>

                {/* KARTA 6: supportit */}
                <div style={styles.card}>
                  <div style={styles.cardHeaderGradient}>
                    <h2 style={styles.cardTitleGradient}>
                      <FormattedMessage id="supportit" />
                    </h2>
                  </div>

                  <div style={styles.sectionSubtitle}>
                    {language === "pl"
                      ? "Jako wieloletni kierownik działów IT w organizacjach o zróżnicowanej skali działalności skutecznie łączyłem kompetencje techniczne z realizacją celów biznesowych. Posiadam bogate doświadczenie w zarządzaniu infrastrukturą IT, wdrażaniu nowych rozwiązań technologicznych oraz koordynacji pracy zespołów specjalistów. Dzięki rozwiniętym umiejętnościom komunikacyjnym efektywnie współpracuję zarówno z użytkownikami, jak i kadrą zarządzającą. Swobodnie poruszam się w środowisku nowoczesnych technologii, systemów operacyjnych i narzędzi wspierających utrzymanie oraz rozwój systemów informatycznych. Umiejętność pracy pod presją czasu, zarządzania priorytetami oraz podejmowania trafnych decyzji w dynamicznym środowisku biznesowym stanowi istotny element mojego doświadczenia zawodowego."
                      : "As a long-standing IT Manager across organizations of various sizes, I successfully combined technical expertise with the achievement of business objectives. I have extensive experience in managing IT infrastructure, implementing new technology solutions, and coordinating the work of technical teams. Strong communication skills enable me to effectively collaborate with both end users and management stakeholders. I am highly experienced with modern technologies, operating systems, and tools supporting the maintenance and development of IT environments. The ability to work under pressure, manage priorities effectively, and make sound decisions in dynamic business environments has been a key part of my professional career."}
                  </div>
                  <br />
                  <ul style={styles.list}>
                    {language === "pl" ? (
                      <>
                        Moje doświadczenie to:
                        <li style={styles.listItem}>
                          <strong>rozwiązywanie problemów:</strong> szybka
                          diagnoza sprzętu IT i minimalizowanie przestojów;
                        </li>
                        <li style={styles.listItem}>
                          <strong>wsparcie użytkowników:</strong> pomoc zdalna,
                          telefoniczna i osobista w instalacji aplikacji i
                          konfiguracji;
                        </li>
                        <li style={styles.listItem}>
                          <strong>utrzymanie systemów:</strong> ciągłe
                          monitorowanie wydajności infrastruktury IT, backupy i
                          aktualizacje;
                        </li>
                        <li style={styles.listItem}>
                          <strong>dokumentacja:</strong> tworzenie raportów
                          serwisowych oraz bazy wiedzy dla użytkowników.
                        </li>
                      </>
                    ) : (
                      <>
                        <li style={styles.listItem}>
                          <strong>Problem solving:</strong> Fast diagnosis of IT
                          hardware issues to minimize operational downtime.
                        </li>
                        <li style={styles.listItem}>
                          <strong>User support:</strong> Remote, phone, and
                          on-site assistance with software and devices.
                        </li>
                        <li style={styles.listItem}>
                          <strong>Maintenance:</strong> Continuous system
                          monitoring, updates, and IT infrastructure security.
                        </li>
                        <li style={styles.listItem}>
                          <strong>Documentation:</strong> Creating issue
                          tracking reports and clear knowledge bases for users.
                        </li>
                      </>
                    )}
                  </ul>
                </div>

                <strong>
                  Jesteś zainteresowany?
                  <br />
                  <a href="/kontakt">skontaktuj się ze mną</a>
                </strong>
                {Offer && (
                  <div style={styles.imageContainer}>
                    <img
                      src={Offer}
                      style={styles.imgStyle}
                      alt="Offer landscape screen"
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </IntlProvider>
      </div>
    </>
  );
};

export default Job;
